/*
 * Native 30-minute call booking. Replaces the external calendar.
 * Pick a date -> pick an available slot -> enter details -> POST /api/booking.
 * Availability and double-booking are enforced server-side (Europe/Copenhagen).
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, Loader2, CalendarDays, Clock, Phone, Video, Users,
  CheckCircle2, ChevronLeft, ChevronRight, ShieldCheck,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageToggle } from "@/components/landing/LanguageToggle";
import { trackClick } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MEETING_ICONS = { phone: Phone, zoom: Video, teams: Users };

const fieldClass =
  "w-full rounded-xl bg-[rgba(8,12,22,0.7)] border border-[var(--line)] px-4 py-3 text-[var(--white)] placeholder:text-[var(--muted-2)] text-sm outline-none transition-colors duration-300 focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--blue)]";
const labelClass = "block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider";

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
};

export const BookingPage = () => {
  const { t, lang } = useLang();
  const b = t.booking;
  const locale = lang === "da" ? "da-DK" : "en-US";

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => addDays(today, 60), [today]);

  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [meetingType, setMeetingType] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "", company_url: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    document.title = lang === "da" ? "Book en tid · ReverseAI.dk" : "Book a call · ReverseAI.dk";
    window.scrollTo(0, 0);
  }, [lang]);

  useEffect(() => {
    if (!selectedDate) return;
    let active = true;
    setLoadingSlots(true);
    setTime("");
    axios
      .get(`${API}/booking/availability`, { params: { date: selectedDate } })
      .then(({ data }) => active && setSlots(data.slots || []))
      .catch(() => active && setSlots([]))
      .finally(() => active && setLoadingSlots(false));
    return () => {
      active = false;
    };
  }, [selectedDate]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const monthCells = useMemo(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const startOffset = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
  }, [view]);

  const isSelectable = (d) => {
    if (!d) return false;
    const wd = d.getDay();
    return wd >= 1 && wd <= 5 && d >= today && d <= maxDate;
  };

  const canPrev = view > new Date(today.getFullYear(), today.getMonth(), 1);
  const canNext =
    new Date(view.getFullYear(), view.getMonth() + 1, 1) <=
    new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

  const weekdays = lang === "da"
    ? ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const fmtWhen = (dateStr, timeStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const endMin = parseInt(timeStr.slice(0, 2), 10) * 60 + parseInt(timeStr.slice(3), 10) + 30;
    const end = `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`;
    const datePart = dt.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return `${datePart} · ${timeStr}-${end}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !time) {
      toast.error(b.errSlot);
      return;
    }
    if (!meetingType) {
      toast.error(b.errType);
      return;
    }
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(b.errRequired);
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error(b.errEmail);
      return;
    }
    const src = new URLSearchParams(window.location.search).get("src") || "book";
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        meeting_type: meetingType,
        date: selectedDate,
        time,
        message: form.message.trim() || null,
        language: lang,
        source_cta: src,
        company_url: form.company_url,
      });
      const mtLabel = (b.meetingTypes.find((x) => x.value === meetingType) || {}).label || meetingType;
      setConfirmed({ when: fmtWhen(selectedDate, time), type: mtLabel });
      setDone(true);
      trackClick("book_time");
      window.scrollTo(0, 0);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(b.errTaken);
        setTime("");
        setLoadingSlots(true);
        try {
          const { data } = await axios.get(`${API}/booking/availability`, { params: { date: selectedDate } });
          setSlots(data.slots || []);
        } catch (_) {
          /* ignore */
        } finally {
          setLoadingSlots(false);
        }
      } else {
        toast.error(b.errFail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] relative overflow-hidden" data-testid="booking-page">
      <div className="noise" />
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" style={{ zIndex: 0 }} />
      <div className="glow-orb animate-glow pointer-events-none" style={{ zIndex: 0, width: 560, height: 420, top: -80, left: "50%", transform: "translateX(-50%)", background: "var(--blue)", opacity: 0.18 }} />

      <header className="relative z-10 mx-auto max-w-5xl px-5 pt-7 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group" data-testid="booking-brand">
          <span className="grid place-items-center h-9 w-9 rounded-lg panel font-display font-bold text-sm text-white">RA</span>
          <span className="font-display font-semibold text-white text-sm">
            ReverseAI<span className="text-[var(--green)]">.dk</span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted hover:text-white transition-colors" data-testid="booking-back-link">
            <ArrowLeft size={15} /> {b.back}
          </Link>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-5 pt-12 pb-24">
        {done ? (
          <div className="glass-card depth-card rounded-3xl p-8 lg:p-12 text-center max-w-2xl mx-auto" data-testid="booking-success">
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-[rgba(54,226,160,0.12)] text-[var(--green)] mb-6 validate-pulse">
              <CheckCircle2 size={32} />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white">{b.successTitle}</h1>
            <p className="mt-4 text-base text-muted leading-relaxed max-w-xl mx-auto">{b.successBody}</p>
            <div className="mt-7 text-left glass rounded-2xl p-5 max-w-md mx-auto space-y-3" data-testid="booking-success-detail">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 shrink-0 rounded-lg bg-[rgba(61,123,255,0.1)] text-[var(--blue-bright)]"><CalendarDays size={16} /></span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{b.successWhen}</div>
                  <div className="text-sm text-white">{confirmed?.when}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 shrink-0 rounded-lg bg-[rgba(54,226,160,0.1)] text-[var(--green)]"><Clock size={16} /></span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{b.successType}</div>
                  <div className="text-sm text-white">{confirmed?.type}</div>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <Link to="/" className="btn-primary mx-auto" data-testid="booking-success-home">
                {b.successHome} <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 mb-5">
              <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--blue-bright)] border border-[var(--line-strong)] rounded-full px-3 py-1">
                <CalendarDays size={13} /> {b.badge}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold leading-[1.07] text-white" data-testid="booking-headline">
              {b.headline}
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted leading-relaxed max-w-2xl">{b.sub}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {b.trust.map((tr) => (
                <span key={tr} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[var(--muted-2)] border border-[var(--line)] rounded-full px-3 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--green)]" /> {tr}
                </span>
              ))}
            </div>

            <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-6">
              {/* Calendar + slots */}
              <div className="glass-card rounded-3xl p-6" data-testid="booking-calendar-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{b.steps.date}</div>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={!canPrev} onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="grid place-items-center h-8 w-8 rounded-lg border border-[var(--line)] text-white disabled:opacity-30 hover:border-[var(--blue)] transition-colors" data-testid="booking-prev-month" aria-label="prev">
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-sm text-white font-medium capitalize min-w-[120px] text-center" data-testid="booking-month-label">
                      {view.toLocaleDateString(locale, { month: "long", year: "numeric" })}
                    </span>
                    <button type="button" disabled={!canNext} onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="grid place-items-center h-8 w-8 rounded-lg border border-[var(--line)] text-white disabled:opacity-30 hover:border-[var(--blue)] transition-colors" data-testid="booking-next-month" aria-label="next">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                  {weekdays.map((w) => (
                    <div key={w} className="text-center font-mono text-[10px] uppercase text-[var(--muted-2)] py-1">{w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {monthCells.map((d, i) => {
                    if (!d) return <div key={`x${i}`} />;
                    const selectable = isSelectable(d);
                    const key = ymd(d);
                    const active = key === selectedDate;
                    return (
                      <button
                        type="button"
                        key={key}
                        disabled={!selectable}
                        onClick={() => setSelectedDate(key)}
                        className={`h-10 rounded-lg text-sm transition-colors duration-200 ${
                          active
                            ? "bg-[var(--blue)] text-white font-semibold"
                            : selectable
                            ? "text-white hover:bg-white/10 border border-[var(--line)]"
                            : "text-[var(--muted-2)] opacity-30 cursor-not-allowed"
                        }`}
                        data-testid={selectable ? `booking-day-${key}` : undefined}
                      >
                        {d.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 pt-5 border-t border-[var(--line)]">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)] mb-3">{b.steps.time}</div>
                  {!selectedDate ? (
                    <p className="text-sm text-muted" data-testid="booking-select-date-prompt">{b.selectDatePrompt}</p>
                  ) : loadingSlots ? (
                    <p className="flex items-center gap-2 text-sm text-muted" data-testid="booking-slots-loading"><Loader2 size={15} className="animate-spin" /> {b.loadingSlots}</p>
                  ) : slots.length === 0 ? (
                    <p className="text-sm text-muted" data-testid="booking-no-slots">{b.noSlots}</p>
                  ) : (
                    <>
                      <div className="text-[11px] text-[var(--muted-2)] mb-2">{b.slotsLabel}</div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
                        {slots.map((s) => (
                          <button
                            type="button"
                            key={s}
                            onClick={() => setTime(s)}
                            className={`py-2 rounded-lg text-sm font-mono transition-colors duration-200 border ${
                              time === s
                                ? "bg-[var(--green)] text-[#05121a] border-[var(--green)] font-semibold"
                                : "text-white border-[var(--line)] hover:border-[var(--green)]"
                            }`}
                            data-testid={`booking-slot-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Details form */}
              <form onSubmit={submit} className="glass-card rounded-3xl p-6 lg:p-7 space-y-4" data-testid="booking-form" noValidate>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{b.steps.details}</div>

                {/* Honeypot */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: 0, height: 0, overflow: "hidden" }}>
                  <label>Company URL<input type="text" tabIndex={-1} autoComplete="off" value={form.company_url} onChange={update("company_url")} data-testid="booking-honeypot" /></label>
                </div>

                <div>
                  <label className={labelClass}>{b.labels.meetingType} *</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="booking-meeting-types">
                    {b.meetingTypes.map((mt) => {
                      const Icon = MEETING_ICONS[mt.value] || Phone;
                      const active = meetingType === mt.value;
                      return (
                        <button
                          type="button"
                          key={mt.value}
                          onClick={() => setMeetingType(mt.value)}
                          className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs transition-colors duration-200 ${
                            active ? "border-[var(--blue)] bg-[rgba(61,123,255,0.1)] text-white" : "border-[var(--line)] text-muted hover:border-[var(--blue)]"
                          }`}
                          data-testid={`booking-meeting-${mt.value}`}
                        >
                          <Icon size={18} className={active ? "text-[var(--blue-bright)]" : ""} />
                          {mt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>{b.labels.name} *</label>
                    <input className={fieldClass} value={form.name} onChange={update("name")} placeholder={b.placeholders.name} data-testid="booking-name-input" />
                  </div>
                  <div>
                    <label className={labelClass}>{b.labels.email} *</label>
                    <input type="email" className={fieldClass} value={form.email} onChange={update("email")} placeholder={b.placeholders.email} data-testid="booking-email-input" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>{b.labels.company} <span className="text-[var(--muted-2)] normal-case">({b.optional})</span></label>
                    <input className={fieldClass} value={form.company} onChange={update("company")} placeholder={b.placeholders.company} data-testid="booking-company-input" />
                  </div>
                  <div>
                    <label className={labelClass}>{b.labels.phone} <span className="text-[var(--muted-2)] normal-case">({b.optional})</span></label>
                    <input className={fieldClass} value={form.phone} onChange={update("phone")} placeholder={b.placeholders.phone} data-testid="booking-phone-input" />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>{b.labels.message} <span className="text-[var(--muted-2)] normal-case">({b.optional})</span></label>
                  <textarea rows={3} className={`${fieldClass} resize-none`} value={form.message} onChange={update("message")} placeholder={b.placeholders.message} data-testid="booking-message-input" />
                </div>

                {selectedDate && time ? (
                  <div className="flex items-center gap-2 text-sm text-white glass rounded-xl px-4 py-3" data-testid="booking-selected-summary">
                    <CalendarDays size={15} className="text-[var(--blue-bright)] shrink-0" />
                    <span>{fmtWhen(selectedDate, time)}</span>
                  </div>
                ) : null}

                <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" data-testid="booking-submit-btn">
                  {loading ? (<><Loader2 size={18} className="animate-spin" /> {b.sending}</>) : (<>{b.submit} <ArrowUpRight size={18} /></>)}
                </button>
                <p className="flex items-center justify-center gap-2 text-center text-[11px] text-[var(--muted-2)] font-mono">
                  <ShieldCheck size={11} /> {b.footnote}
                </p>
              </form>
            </div>
          </>
        )}
      </main>
    </div>
  );
};
